import {
  RegisterAssetVariantsResponse,
  type AdminAsset,
  type AssetRef,
  type HeroImageComposition,
} from '@diana/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Move, RotateCcw } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useAdminApi } from '../../app/AdminApiContext.js';
import {
  prepareResponsiveImage,
  type PreparedResponsiveVariant,
} from '../../lib/image-variants.js';
import {
  createMediaUploadController,
  type MediaUploadController,
  type UploadFile,
} from '../../lib/media-upload.js';
import { Button, Typography } from '../../ui/index.js';
import { cropForFocus, cropPixelSize, type NormalizedCrop } from './image-crop.js';
import { imageSlotProfile } from './image-slots.js';
import './ContextualMediaField.css';
import './WeddingHeroImageField.css';

export type WeddingHeroImagePreview = {
  desktopUrl: string;
  mobileUrl: string;
  desktopCrop?: NormalizedCrop;
  mobileCrop?: NormalizedCrop;
};
type Placement = 'desktop' | 'mobile';
type Dimensions = { width: number; height: number };
type Draft = {
  selectionId: number;
  file: UploadFile;
  source?: AssetRef;
  crops?: Record<Placement, NormalizedCrop>;
};
const placements = ['desktop', 'mobile'] as const;
const ratios = { desktop: { width: 4, height: 5 }, mobile: { width: 4, height: 3 } };
const labels = { desktop: 'Desktop', mobile: 'Mobile' };
const previewUrl = (media: AssetRef) => `/api/v1/admin/assets/${media.assetId}/preview`;

export function WeddingHeroImageField({
  assets,
  value,
  legacyMedia,
  onChange,
  onPreviewChange,
}: {
  assets: readonly AdminAsset[];
  value?: HeroImageComposition;
  legacyMedia?: AssetRef;
  onChange(value: HeroImageComposition): void;
  onPreviewChange?(value: WeddingHeroImagePreview | undefined): void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const operation = useRef(0);
  const pending = useRef<AbortController | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<AdminAsset[]>([]);
  const media = value?.desktop.media ?? legacyMedia;
  const asset = [...assets, ...uploaded].find((candidate) => candidate.id === media?.assetId);
  const alt = media?.altOverride ?? asset?.alt ?? '';
  const [altDraft, setAltDraft] = useState(alt);
  useEffect(() => {
    setAltDraft(alt);
  }, [alt]);
  useEffect(
    () => () => {
      operation.current += 1;
      pending.current?.abort();
    },
    [],
  );

  const edit = async () => {
    if (!value?.source) return;
    const attempt = ++operation.current;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/admin/assets/${value.source.assetId}/original`, {
        credentials: 'same-origin',
        redirect: 'error',
        signal: controller.signal,
      });
      if (!response.ok)
        throw new Error('The original could not be opened. Try again or replace the image.');
      const blob = await response.blob();
      if (attempt !== operation.current) return;
      const original = [...assets, ...uploaded].find(
        (candidate) => candidate.id === value.source?.assetId,
      );
      const name =
        original?.originalFilename ??
        `wedding-original.${blob.type === 'image/jpeg' ? 'jpg' : 'webp'}`;
      setDraft({
        selectionId: attempt,
        file: Object.assign(blob, { name }),
        source: value.source,
        crops: { desktop: value.desktop.crop, mobile: value.mobile.crop },
      });
    } catch (caught) {
      if (attempt === operation.current) setError(errorMessage(caught));
    } finally {
      if (attempt === operation.current) setLoading(false);
    }
  };
  const cancelLoading = () => {
    operation.current += 1;
    pending.current?.abort();
    setLoading(false);
    setError(null);
  };
  const commitAlt = () => {
    if (!value) return;
    if (!altDraft.trim()) {
      setAltDraft(alt);
      return;
    }
    if (altDraft.trim() === alt) return;
    const shared = { altOverride: altDraft.trim() };
    onChange({
      ...value,
      desktop: { ...value.desktop, media: { ...value.desktop.media, ...shared } },
      mobile: { ...value.mobile, media: { ...value.mobile.media, ...shared } },
    });
  };

  return (
    <section
      className="contextual-media-field wedding-hero-image"
      aria-label="Wedding hero photograph"
    >
      <header className="contextual-media-field__header">
        <Typography as="h3" variant="h3">
          Hero photograph
        </Typography>
        <Typography variant="small">One original, two independent compositions.</Typography>
      </header>
      <input
        ref={input}
        className="contextual-media-field__file-input"
        type="file"
        accept="image/jpeg,image/webp"
        aria-label="Choose wedding hero photograph"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = '';
          if (!file) return;
          cancelLoading();
          if (
            !['image/jpeg', 'image/webp'].includes(file.type) ||
            file.size > 16 * 1024 * 1024 ||
            file.size === 0
          ) {
            setError('Choose a JPEG or WebP original no larger than 16 MB.');
            return;
          }
          setDraft({ selectionId: operation.current, file });
        }}
      />
      {draft ? (
        <WeddingCropEditor
          key={draft.selectionId}
          draft={draft}
          initialAlt={altDraft}
          onPreviewChange={onPreviewChange}
          onCancel={() => setDraft(null)}
          onReady={(composition, saved) => {
            setUploaded((current) => [...current, ...saved]);
            onChangeRef.current(composition);
            setDraft(null);
          }}
        />
      ) : (
        <div className="wedding-hero-image__summary">
          {value ? (
            <div className="wedding-hero-image__thumbnails">
              {placements.map((placement) => (
                <figure key={placement}>
                  <img
                    src={previewUrl(value[placement].media)}
                    alt=""
                    style={{
                      aspectRatio: `${ratios[placement].width}/${ratios[placement].height}`,
                    }}
                  />
                  <figcaption>
                    {labels[placement]} · {placement === 'desktop' ? '4:5' : '4:3'}
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : media ? (
            <div className="wedding-hero-image__thumbnails wedding-hero-image__thumbnails--single">
              <figure>
                <img src={previewUrl(media)} alt="" />
                <figcaption>Current image</figcaption>
              </figure>
            </div>
          ) : null}
          <div className="wedding-hero-image__actions">
            <Button
              variant="primary"
              leadingIcon={ImagePlus}
              onClick={() => input.current?.click()}
            >
              {media ? 'Replace image' : 'Upload image'}
            </Button>
            {value?.source ? (
              <Button variant="secondary" disabled={loading} onClick={() => void edit()}>
                {loading ? 'Opening original…' : 'Edit crops'}
              </Button>
            ) : media ? (
              <Typography variant="caption">
                Replace the image to save an original and edit both crops.
              </Typography>
            ) : (
              <Typography variant="caption">JPEG or WebP · maximum 16 MB</Typography>
            )}
            {loading ? (
              <Button variant="quiet" onClick={cancelLoading}>
                Cancel
              </Button>
            ) : null}
          </div>
          {value ? (
            <label className="contextual-media-alt">
              <span>Alt text</span>
              <input
                className="dui-input"
                maxLength={500}
                value={altDraft}
                onChange={(event) => setAltDraft(event.currentTarget.value)}
                onBlur={commitAlt}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                }}
              />
            </label>
          ) : null}
        </div>
      )}
      {error ? (
        <p className="wedding-hero-image__error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
}

function WeddingCropEditor({
  draft,
  initialAlt,
  onReady,
  onCancel,
  onPreviewChange,
}: {
  draft: Draft;
  initialAlt: string;
  onReady(value: HeroImageComposition, assets: AdminAsset[]): void;
  onCancel(): void;
  onPreviewChange?(value: WeddingHeroImagePreview | undefined): void;
}) {
  const api = useAdminApi();
  const queryClient = useQueryClient();
  const id = useId();
  const [url, setUrl] = useState<string>();
  const [source, setSource] = useState<Dimensions>();
  const [crops, setCrops] = useState<Record<Placement, NormalizedCrop> | undefined>(draft.crops);
  const [placement, setPlacement] = useState<Placement>('desktop');
  const [alt, setAlt] = useState(initialAlt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [progress, setProgress] = useState('');
  const sourceRef = useRef(draft.source);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const controllers = useRef<MediaUploadController[]>([]);
  const operation = useRef(0);
  const previewCallback = useRef(onPreviewChange);
  previewCallback.current = onPreviewChange;
  const drag = useRef<{
    pointerId: number;
    x: number;
    y: number;
    crop: NormalizedCrop;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    let active = true;
    const preview = URL.createObjectURL(draft.file);
    setUrl(preview);
    void createImageBitmap(draft.file, { imageOrientation: 'from-image' })
      .then((bitmap) => {
        if (active) {
          const dimensions = { width: bitmap.width, height: bitmap.height };
          setSource(dimensions);
          setCrops(
            draft.crops ?? {
              desktop: defaultCrop(dimensions, 'desktop'),
              mobile: defaultCrop(dimensions, 'mobile'),
            },
          );
        }
        bitmap.close();
      })
      .catch(() => {
        if (active) setError('This photograph could not be opened.');
      });
    return () => {
      active = false;
      URL.revokeObjectURL(preview);
    };
  }, [draft]);
  useEffect(
    () => () => {
      operation.current += 1;
      controllers.current.forEach((controller) => controller.dispose());
      previewCallback.current?.(undefined);
    },
    [],
  );
  useEffect(() => {
    if (url && crops)
      previewCallback.current?.({
        desktopUrl: url,
        mobileUrl: url,
        desktopCrop: crops.desktop,
        mobileCrop: crops.mobile,
      });
  }, [url, crops]);

  const crop = crops?.[placement];
  const maximum = source ? defaultCrop(source, placement) : undefined;
  const zoom = maximum && crop ? maximum.width / crop.width : 1;
  const enoughPixels =
    source &&
    crops &&
    placements.every((target) => {
      const size = cropPixelSize(crops[target], source);
      return (
        size.width >= (target === 'desktop' ? 1600 : 960) &&
        size.height >= (target === 'desktop' ? 2000 : 720)
      );
    });
  const changeCrop = (next: NormalizedCrop) =>
    setCrops((current) => current && { ...current, [placement]: next });
  const move = (x: number, y: number) => {
    if (crop)
      changeCrop({ ...crop, x: clamp(x, 0, 1 - crop.width), y: clamp(y, 0, 1 - crop.height) });
  };

  const save = async () => {
    if (!crops || !source || busy) return;
    const attempt = ++operation.current;
    controllers.current.forEach((controller) => controller.dispose());
    controllers.current = [];
    setBusy(true);
    setError(undefined);
    const saved: AdminAsset[] = [];
    const assertCurrent = () => {
      if (attempt !== operation.current) throw new Error('Cancelled');
    };
    const upload = async (file: UploadFile, prepared?: PreparedResponsiveVariant) => {
      assertCurrent();
      const controller = createMediaUploadController({
        api,
        ...(prepared
          ? {
              prepare: async () => ({
                checksum: prepared.checksum,
                width: prepared.width,
                height: prepared.height,
              }),
            }
          : {}),
      });
      controllers.current.push(controller);
      if (!controller.select({ file, alt: alt.trim() }))
        throw controller.getSnapshot().error ?? new Error('Invalid photograph');
      await controller.start();
      assertCurrent();
      const result = controller.getSnapshot();
      if (result.status !== 'Ready' || !result.asset)
        throw result.error ?? new Error('Photograph upload failed');
      saved.push(result.asset);
      return result.asset;
    };
    try {
      if (!sourceRef.current) {
        setProgress('Saving original');
        const original = await upload(draft.file);
        assertCurrent();
        sourceRef.current = { assetId: original.id };
      }
      const media = {} as Record<Placement, AssetRef>;
      for (const target of placements) {
        setProgress(`Saving ${target} crop`);
        const filename = draft.file.name.replace(/(\.[^.]+)$/, `-${target}$1`);
        const namedFile = new File([draft.file], filename, { type: draft.file.type });
        const variants = await prepareResponsiveImage(
          namedFile,
          imageSlotProfile(target === 'desktop' ? 'standard' : 'compact'),
          { crop: crops[target] },
        );
        assertCurrent();
        const uploaded: { assetId: string; width: number }[] = [];
        for (const variant of variants) {
          const asset = await upload(variant.file, variant);
          assertCurrent();
          uploaded.push({ assetId: asset.id, width: variant.width });
        }
        const canonical = uploaded.at(-1);
        if (!canonical) throw new Error('No crop variants were prepared');
        await api.request(`/api/v1/admin/assets/${canonical.assetId}/variants`, {
          method: 'POST',
          body: { variants: uploaded },
          schema: RegisterAssetVariantsResponse,
        });
        assertCurrent();
        media[target] = { assetId: canonical.assetId, altOverride: alt.trim() };
      }
      try {
        await queryClient.invalidateQueries({ queryKey: ['assets'] });
      } catch {
        /* Confirmed assets remain available. */
      }
      assertCurrent();
      onReadyRef.current(
        {
          source: sourceRef.current,
          desktop: { media: media.desktop, crop: crops.desktop },
          mobile: { media: media.mobile, crop: crops.mobile },
        },
        saved,
      );
    } catch (caught) {
      if (attempt === operation.current) setError(errorMessage(caught));
    } finally {
      if (attempt === operation.current) setBusy(false);
    }
  };

  return (
    <section className="contextual-crop wedding-hero-crop" aria-label="Edit wedding hero crops">
      <header className="contextual-crop__header">
        <div>
          <Typography as="h4" variant="h3">
            Compose your photograph
          </Typography>
          <Typography variant="small">Adjust each crop. Both are saved together.</Typography>
        </div>
      </header>
      <div className="wedding-hero-crop__tabs" role="tablist" aria-label="Hero crop format">
        {placements.map((target) => (
          <button
            type="button"
            key={target}
            role="tab"
            id={`${id}-${target}-tab`}
            aria-selected={placement === target}
            aria-controls={`${id}-panel`}
            disabled={busy}
            onClick={() => {
              drag.current = null;
              setPlacement(target);
            }}
          >
            {labels[target]} · {target === 'desktop' ? '4:5' : '4:3'}
          </button>
        ))}
      </div>
      <div
        id={`${id}-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-${placement}-tab`}
        className="contextual-crop__canvas"
      >
        {source && url && crop ? (
          <div
            className="contextual-crop-stage"
            style={{
              aspectRatio: `${source.width}/${source.height}`,
              width: `min(100%, ${Math.round(Math.min(760, (source.width / source.height) * 480))}px)`,
            }}
          >
            <img src={url} alt="" />
            <button
              type="button"
              className="contextual-crop-stage__window"
              disabled={busy}
              aria-label={`${labels[placement]} crop area. Drag or use arrow keys to reposition`}
              style={{
                left: `${crop.x * 100}%`,
                top: `${crop.y * 100}%`,
                width: `${crop.width * 100}%`,
                height: `${crop.height * 100}%`,
              }}
              onPointerDown={(event) => {
                const bounds = event.currentTarget.parentElement?.getBoundingClientRect();
                if (!bounds?.width || !bounds.height) return;
                event.currentTarget.setPointerCapture?.(event.pointerId);
                drag.current = {
                  pointerId: event.pointerId,
                  x: event.clientX,
                  y: event.clientY,
                  crop,
                  width: bounds.width,
                  height: bounds.height,
                };
              }}
              onPointerMove={(event) => {
                const start = drag.current;
                if (!start || start.pointerId !== event.pointerId) return;
                move(
                  start.crop.x + (event.clientX - start.x) / start.width,
                  start.crop.y + (event.clientY - start.y) / start.height,
                );
              }}
              onPointerUp={() => {
                drag.current = null;
              }}
              onPointerCancel={() => {
                drag.current = null;
              }}
              onLostPointerCapture={() => {
                drag.current = null;
              }}
              onKeyDown={(event) => {
                const step = event.shiftKey ? 0.05 : 0.01;
                const offset = {
                  ArrowLeft: [-step, 0],
                  ArrowRight: [step, 0],
                  ArrowUp: [0, -step],
                  ArrowDown: [0, step],
                }[event.key];
                if (offset) {
                  event.preventDefault();
                  move(crop.x + offset[0], crop.y + offset[1]);
                }
              }}
            >
              <Move size={20} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <Typography variant="small">Opening photograph…</Typography>
        )}
      </div>
      <div className="contextual-crop__details">
        <label className="contextual-crop__zoom">
          <span>{labels[placement]} zoom</span>
          <input
            type="range"
            aria-label={`${labels[placement]} crop zoom`}
            min="1"
            max="3"
            step="0.05"
            value={zoom}
            disabled={busy || !source}
            onChange={(event) => {
              if (!source || !crop) return;
              changeCrop(
                cropForFocus({
                  sourceWidth: source.width,
                  sourceHeight: source.height,
                  targetWidth: ratios[placement].width,
                  targetHeight: ratios[placement].height,
                  focusX: crop.x + crop.width / 2,
                  focusY: crop.y + crop.height / 2,
                  zoom: Number(event.currentTarget.value),
                }),
              );
            }}
          />
        </label>
        <div className="wedding-hero-image__thumbnails">
          {url && crops
            ? placements.map((target) => (
                <figure key={target}>
                  <CropThumbnail url={url} crop={crops[target]} placement={target} />
                  <figcaption>
                    {labels[target]} · {target === 'desktop' ? '4:5' : '4:3'}
                  </figcaption>
                </figure>
              ))
            : null}
        </div>
        <label className="contextual-media-alt">
          <span>Alt text</span>
          <input
            className="dui-input"
            maxLength={500}
            value={alt}
            disabled={busy}
            onChange={(event) => setAlt(event.currentTarget.value)}
          />
        </label>
        {!enoughPixels && source ? (
          <Typography variant="caption">
            Keep at least 1600 × 2000 px for desktop and 960 × 720 px for mobile. Reduce zoom or
            choose a larger original.
          </Typography>
        ) : null}
        {error ? (
          <p className="wedding-hero-image__error" role="alert">
            {error}
          </p>
        ) : null}
        {busy ? <p role="status">{progress}…</p> : null}
      </div>
      <footer className="contextual-crop__actions">
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          leadingIcon={RotateCcw}
          disabled={busy || !source}
          onClick={() => {
            if (source) changeCrop(defaultCrop(source, placement));
          }}
        >
          Reset crop
        </Button>
        <Button
          variant="primary"
          disabled={busy || !enoughPixels || !alt.trim() || alt.trim().length > 500}
          onClick={() => void save()}
        >
          {busy ? 'Saving crops…' : 'Save crops'}
        </Button>
      </footer>
    </section>
  );
}
function CropThumbnail({
  url,
  crop,
  placement,
}: {
  url: string;
  crop: NormalizedCrop;
  placement: Placement;
}) {
  return (
    <div
      className="wedding-hero-crop__thumbnail"
      data-testid={`${placement}-crop-preview`}
      data-crop={JSON.stringify(crop)}
      style={{ aspectRatio: `${ratios[placement].width}/${ratios[placement].height}` }}
    >
      <img
        src={url}
        alt=""
        style={{
          width: `${100 / crop.width}%`,
          height: `${100 / crop.height}%`,
          left: `${(-crop.x / crop.width) * 100}%`,
          top: `${(-crop.y / crop.height) * 100}%`,
        }}
      />
    </div>
  );
}
function defaultCrop(source: Dimensions, placement: Placement) {
  return cropForFocus({
    sourceWidth: source.width,
    sourceHeight: source.height,
    targetWidth: ratios[placement].width,
    targetHeight: ratios[placement].height,
    focusX: 0.5,
    focusY: 0.5,
    zoom: 1,
  });
}
function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}
function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'The crops could not be saved. Please try again.';
}
